/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import {GridItemProps} from "@insite/mobius/GridItem";
import {GridContainer, GridItem} from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {GridContainerProps} from "@monsma/MobiusOverrides/GridContainer/GridContainer";
import {mergeWithCss} from "@monsma/XDL/Utilities";
import chunk from "lodash/chunk";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface XStyles {
    attributeTypesContainer: GridContainerProps;
    attributeTypesGridItem: GridItemProps;
}

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    xStyles?: XStyles;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            {brand && (
                <Typography data-test-selector="brand_item" as="p" {...mergeWithCss(styles.attributeTypeValuesText, {css: css`margin-bottom: 15px`})}>
                    <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                        {translate("Brand")}&nbsp;:&nbsp;
                    </Typography>
                    <span data-test-selector="brand_item_value">{brand.name}</span>
                </Typography>
            )}
            {attributeTypes && attributeTypes.length > 0 && (
                <GridContainer {...styles.xStyles?.attributeTypesContainer}>
                    {chunk(attributeTypes, 6).map((attributeTypesChunk, index) => (
                        <GridItem {...styles.xStyles?.attributeTypesGridItem} key={`attributes_item_${index}`}>
                            {attributeTypesChunk.map(attributeType => (
                                <Typography key={attributeType.id.toString()}
                                            data-test-selector="attributes_item"
                                            data-attributetypeid={attributeType.id}
                                            {...styles.attributeTypeValuesText}
                                            as="p">
                                    <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                                        {attributeType.label || attributeType.name}&nbsp;:&nbsp;
                                    </Typography>
                                    <span data-test-selector="attributes_item_value">
                                    {(attributeType.attributeValues ?? [])
                                        .map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </span>
                                </Typography>
                            ))}
                        </GridItem>
                    ))}
                </GridContainer>                
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
